// CBOR (RFC 8949) の最小限の符号化・復号。
//
// 制御メッセージ (protocol/src/control.rs) が使うのは、整数・文字列・真偽・null・配列・
// 「文字列を鍵にした map」だけなので、その範囲だけを自前で持つ (外部ライブラリを足さない)。
// Rust 側 (ciborium) は定長 (definite length) で出力するので、不定長は復号しない。
// 浮動小数点・タグ・バイト列は現状の語彙に無いため未対応 (出会ったら例外にする)。

export type CborValue =
  | number
  | string
  | boolean
  | null
  | CborValue[]
  | { [key: string]: CborValue };

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", { fatal: true });

class Writer {
  private chunks: Uint8Array[] = [];
  private length = 0;

  byte(b: number): void {
    this.chunks.push(Uint8Array.of(b));
    this.length += 1;
  }

  bytes(b: Uint8Array): void {
    this.chunks.push(b);
    this.length += b.length;
  }

  // 主型 (major type) と引数 (長さ・整数値) を書く。
  head(major: number, argument: number): void {
    const m = major << 5;
    if (argument < 24) {
      this.byte(m | argument);
    } else if (argument < 0x100) {
      this.byte(m | 24);
      this.byte(argument);
    } else if (argument < 0x1_0000) {
      this.byte(m | 25);
      this.bytes(Uint8Array.of(argument >>> 8, argument & 0xff));
    } else if (argument < 0x1_0000_0000) {
      this.byte(m | 26);
      this.bytes(
        Uint8Array.of((argument >>> 24) & 0xff, (argument >>> 16) & 0xff, (argument >>> 8) & 0xff, argument & 0xff),
      );
    } else {
      throw new RangeError(`CBOR: 2^32 以上の値は扱わない: ${argument}`);
    }
  }

  finish(): Uint8Array {
    const out = new Uint8Array(this.length);
    let offset = 0;
    for (const c of this.chunks) {
      out.set(c, offset);
      offset += c.length;
    }
    return out;
  }
}

function encodeInto(w: Writer, value: CborValue): void {
  if (value === null) {
    w.byte(0xf6);
  } else if (value === true) {
    w.byte(0xf5);
  } else if (value === false) {
    w.byte(0xf4);
  } else if (typeof value === "number") {
    if (!Number.isInteger(value)) {
      throw new TypeError(`CBOR: 整数以外は扱わない: ${value}`);
    }
    if (value >= 0) {
      w.head(0, value);
    } else {
      w.head(1, -1 - value);
    }
  } else if (typeof value === "string") {
    const bytes = textEncoder.encode(value);
    w.head(3, bytes.length);
    w.bytes(bytes);
  } else if (Array.isArray(value)) {
    w.head(4, value.length);
    for (const v of value) encodeInto(w, v);
  } else if (typeof value === "object") {
    const keys = Object.keys(value);
    w.head(5, keys.length);
    for (const k of keys) {
      encodeInto(w, k);
      encodeInto(w, value[k] as CborValue);
    }
  } else {
    throw new TypeError(`CBOR: 扱えない値: ${String(value)}`);
  }
}

/** 値を CBOR のバイト列にする。 */
export function encode(value: CborValue): Uint8Array {
  const w = new Writer();
  encodeInto(w, value);
  return w.finish();
}

class Reader {
  private pos = 0;
  constructor(private readonly buf: Uint8Array) {}

  private need(n: number): void {
    if (this.pos + n > this.buf.length) {
      throw new RangeError("CBOR: データが途中で終わっている");
    }
  }

  byte(): number {
    this.need(1);
    return this.buf[this.pos++]!;
  }

  slice(n: number): Uint8Array {
    this.need(n);
    const s = this.buf.subarray(this.pos, this.pos + n);
    this.pos += n;
    return s;
  }

  // 追加情報 (additional information) から引数を読む。
  argument(info: number): number {
    if (info < 24) return info;
    if (info === 24) return this.byte();
    if (info === 25) return (this.byte() << 8) | this.byte();
    if (info === 26) {
      return ((this.byte() << 24) >>> 0) + ((this.byte() << 16) | (this.byte() << 8) | this.byte());
    }
    if (info === 27) {
      const hi = this.argument(26);
      const lo = this.argument(26);
      const v = hi * 0x1_0000_0000 + lo;
      if (!Number.isSafeInteger(v)) throw new RangeError("CBOR: 2^53 以上の整数は扱わない");
      return v;
    }
    if (info === 31) throw new RangeError("CBOR: 不定長は扱わない");
    throw new RangeError(`CBOR: 予約された追加情報: ${info}`);
  }

  value(): CborValue {
    const initial = this.byte();
    const major = initial >> 5;
    const info = initial & 0x1f;
    switch (major) {
      case 0:
        return this.argument(info);
      case 1:
        return -1 - this.argument(info);
      case 2:
        throw new RangeError("CBOR: バイト列は制御メッセージの語彙に無い");
      case 3: {
        const n = this.argument(info);
        return textDecoder.decode(this.slice(n));
      }
      case 4: {
        const n = this.argument(info);
        const out: CborValue[] = [];
        for (let i = 0; i < n; i++) out.push(this.value());
        return out;
      }
      case 5: {
        const n = this.argument(info);
        const out: { [key: string]: CborValue } = {};
        for (let i = 0; i < n; i++) {
          const k = this.value();
          if (typeof k !== "string") throw new TypeError("CBOR: map の鍵は文字列だけを扱う");
          out[k] = this.value();
        }
        return out;
      }
      case 6:
        throw new RangeError("CBOR: タグは扱わない");
      case 7:
        if (info === 20) return false;
        if (info === 21) return true;
        if (info === 22) return null;
        throw new RangeError(`CBOR: 扱えない単純値・浮動小数点: ${info}`);
      default:
        throw new RangeError(`CBOR: 不明な主型 ${major}`);
    }
  }

  get remaining(): number {
    return this.buf.length - this.pos;
  }
}

/** CBOR のバイト列を値に戻す。末尾に余りがあれば例外。 */
export function decode(buf: Uint8Array): CborValue {
  const r = new Reader(buf);
  const v = r.value();
  if (r.remaining !== 0) throw new RangeError(`CBOR: 末尾に余りが ${r.remaining} バイト`);
  return v;
}
